import { distinctReasonIds } from '@nvd/claim-shared';
import type {
  Address,
  ClaimItemInput,
  ClaimOutcome,
  OrderView,
  PreviewResponse,
  RefundBreakdown,
  ResolutionOption,
  SubmitClaimRequest,
} from '@nvd/claim-shared';


export type Step = 'find' | 'otp' | 'status' | 'items' | 'issue' | 'resolution' | 'reorder' | 'refund' | 'storeCredit' | 'success';

export interface UploadRef {
  uploadId: string;
  name: string;
  /** Local object URL for an image thumbnail; never sent to the server. */
  previewUrl?: string;
}

export interface DraftItem {
  quantity: number;
  /** A reason id from the shop's config. */
  reasonId: string;
  /** What the customer typed, for a free-text reason. */
  note: string;
}

export interface Draft {
  /** Selected line item id → quantity, reason and note. An item is selected exactly when it has a reason. */
  items: Record<string, DraftItem>;
  notes: string;
  uploads: UploadRef[];
}

export interface State {
  step: Step;
  history: Step[];
  /** Message shown on the first screen, e.g. after a session expires. */
  notice: string | null;
  email: string;
  challenge: string | null;
  otpDelivered: boolean;
  view: OrderView | null;
  draft: Draft;
  resolution: ResolutionOption | null;
  /** What the refund or store credit step shows; both are worth what Shopify would refund. */
  refund: RefundBreakdown | null;
  reorderAddress: Address | null;
  autoReorder: boolean;
  outcome: ClaimOutcome | null;
}

export type Action =
  | { type: 'OTP_REQUIRED'; email: string; challenge: string; delivered: boolean }
  | { type: 'ORDER_LOADED'; view: OrderView }
  /** An empty reason id deselects the item. `quantity` is the default used when it becomes selected. */
  | { type: 'SET_ITEM_REASON'; id: string; reasonId: string; quantity: number }
  | { type: 'SET_ITEM_NOTE'; id: string; note: string }
  | { type: 'SET_ITEM_QUANTITY'; id: string; quantity: number }
  | { type: 'SET_NOTES'; notes: string }
  | { type: 'ADD_UPLOAD'; upload: UploadRef }
  | { type: 'REMOVE_UPLOAD'; uploadId: string }
  | { type: 'GO'; step: Step }
  | { type: 'PREVIEWED'; resolution: ResolutionOption; preview: PreviewResponse }
  | { type: 'SUBMITTED'; outcome: ClaimOutcome }
  | { type: 'BACK' }
  | { type: 'RESET'; notice?: string };

const emptyDraft: Draft = { items: {}, notes: '', uploads: [] };

export const initialState: State = {
  step: 'find',
  history: [],
  notice: null,
  email: '',
  challenge: null,
  otpDelivered: false,
  view: null,
  draft: emptyDraft,
  resolution: null,
  refund: null,
  reorderAddress: null,
  autoReorder: false,
  outcome: null,
};

const go = (state: State, step: Step): State => ({ ...state, step, history: [...state.history, state.step] });

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'OTP_REQUIRED':
      return {
        ...go(state, 'otp'),
        notice: null,
        email: action.email,
        challenge: action.challenge,
        otpDelivered: action.delivered,
      };

    case 'ORDER_LOADED': {
      const blocked = action.view.existingClaim !== null || !action.view.eligibility.ok;
      return { ...state, notice: null, view: action.view, challenge: null, draft: emptyDraft, step: blocked ? 'status' : 'items', history: [] };
    }

    case 'SET_ITEM_REASON': {
      const items = { ...state.draft.items };
      if (!action.reasonId) delete items[action.id];
      else {
        const current = items[action.id];
        items[action.id] = {
          quantity: current?.quantity ?? action.quantity,
          reasonId: action.reasonId,
          // A different reason drops the note that belonged to the old one.
          note: current?.reasonId === action.reasonId ? current.note : '',
        };
      }
      return { ...state, draft: { ...state.draft, items } };
    }

    case 'SET_ITEM_NOTE': {
      const current = state.draft.items[action.id];
      if (!current) return state;
      const items = { ...state.draft.items, [action.id]: { ...current, note: action.note } };
      return { ...state, draft: { ...state.draft, items } };
    }

    case 'SET_ITEM_QUANTITY': {
      const current = state.draft.items[action.id];
      if (!current) return state;
      const items = { ...state.draft.items, [action.id]: { ...current, quantity: action.quantity } };
      return { ...state, draft: { ...state.draft, items } };
    }

    case 'SET_NOTES':
      return { ...state, draft: { ...state.draft, notes: action.notes } };

    case 'ADD_UPLOAD':
      return { ...state, draft: { ...state.draft, uploads: [...state.draft.uploads, action.upload] } };

    case 'REMOVE_UPLOAD':
      return {
        ...state,
        draft: { ...state.draft, uploads: state.draft.uploads.filter((upload) => upload.uploadId !== action.uploadId) },
      };

    case 'GO':
      return go(state, action.step);

    case 'PREVIEWED': {
      const base = { ...state, resolution: action.resolution, refund: null, autoReorder: false };
      if (action.preview.kind === 'auto_refund') return { ...go(base, 'refund'), refund: action.preview.refund };
      if (action.preview.kind === 'auto_store_credit') return { ...go(base, 'storeCredit'), refund: action.preview.credit };
      if (action.resolution.kind === 'reorder') {
        const address =
          action.preview.kind === 'auto_reorder' ? action.preview.address : (state.view?.order.shippingAddress ?? null);
        return { ...go(base, 'reorder'), reorderAddress: address, autoReorder: action.preview.kind === 'auto_reorder' };
      }
      return base;
    }

    case 'SUBMITTED':
      return { ...state, outcome: action.outcome, step: 'success', history: [] };

    case 'BACK': {
      const previous = state.history.at(-1);
      return previous ? { ...state, step: previous, history: state.history.slice(0, -1) } : state;
    }

    case 'RESET':
      return { ...initialState, notice: action.notice ?? null };
  }
}

export const selectionOf = (draft: Draft): ClaimItemInput[] =>
  Object.entries(draft.items).map(([lineItemId, { quantity, reasonId, note }]) => ({
    lineItemId,
    quantity,
    reasonId,
    note: note.trim(),
  }));

/** The distinct reason ids the customer picked, which drive the notices, the requirements and the options. */
export const reasonIdsOf = (draft: Draft): string[] => distinctReasonIds(selectionOf(draft));

/** No `resolutionId` when the shop doesn't let customers choose: the claim is sent from the issue step. */
export function buildSubmitRequest(draft: Draft, resolutionId?: string, shippingAddress?: Address): SubmitClaimRequest {
  return {
    items: selectionOf(draft),
    ...(resolutionId ? { resolutionId } : {}),
    notes: draft.notes.trim(),
    uploadIds: draft.uploads.map((upload) => upload.uploadId),
    ...(shippingAddress ? { shippingAddress } : {}),
  };
}
